/**
 * FAQAccordion which inherits from BaseAccordion
 * Styling and icons match ReportAccordion for consistency
 * Features edit and delete actions alongside expand/collapse
 */

import React, { useState, forwardRef, ReactNode } from 'react'
import BaseAccordion from '../../ui_reusable/BaseAccordion'
import { HeaderLarge500Style } from '../../../styles/Text.styles'
import CustomIcons from '../../ui_reusable/CustomIcons'
import theme from '../../../styles/theme'

import {
  AccordionContainer,
  PanelContainer,
  DynamicHeader,
  ActionButtonsContainer,
  HeaderTitleContainer,
  MainText,
} from './styles/FAQAccordion.styles'
import { LineDivider } from '../../ui_reusable/styles/LineDivider.styles'

interface FAQAccordionProps {
  children: ReactNode
  onContextReady?: () => void
  id: string
  defaultActiveKey?: string | string[] | null
  contextIdGenerated?: (id: string) => void
}

interface FAQPanelProps {
  header?: string | ReactNode
  children?: ReactNode
  text?: string
  onEdit?: () => void
  onDelete?: () => void
  showActions?: boolean
  eventKey?: string
  noCollapse?: boolean
}

interface FAQPanelRef {
  tryCollapse: () => void
}

const FAQAccordion: React.FC<FAQAccordionProps> = ({
  children,
  onContextReady,
  ...props
}) => {
  return (
    <AccordionContainer>
      <BaseAccordion {...props}>{children}</BaseAccordion>
    </AccordionContainer>
  )
}

/**
 * Panel subcomponent for FAQAccordion
 * @param {boolean} showActions - Controls visibility of edit/delete buttons
 */
const Panel = forwardRef<FAQPanelRef, FAQPanelProps>(
  (
    {
      header,
      children,
      text, // Add text prop
      onEdit,
      onDelete,
      showActions = true, // Add showActions prop with default true
      ...props
    },
    ref,
  ) => {
    // Add state to track mousedown position and time
    const [mouseDownPos, setMouseDownPos] = useState({ x: 0, y: 0 })
    const [mouseDownTime, setMouseDownTime] = useState(0)

    const handleMouseDown = (e: React.MouseEvent) => {
      setMouseDownPos({ x: e.clientX, y: e.clientY })
      setMouseDownTime(Date.now())
    }

    const handleClick = (e: React.MouseEvent, onToggle: () => void) => {
      // Calculate distance and time of mouse movement
      const distance = Math.sqrt(
        Math.pow(e.clientX - mouseDownPos.x, 2) +
          Math.pow(e.clientY - mouseDownPos.y, 2),
      )
      const timeElapsed = Date.now() - mouseDownTime

      // If movement is small and quick, treat as click
      if (distance < 5 && timeElapsed < 200) {
        onToggle()
      }
    }

    return (
      <PanelContainer>
        <BaseAccordion.Panel
          {...props}
          ref={ref}
          renderHeader={({ isActive, onToggle }) => (
            <DynamicHeader
              onMouseDown={handleMouseDown}
              onClick={(e) => handleClick(e, onToggle)}
            >
              <HeaderTitleContainer>
                <span css={HeaderLarge500Style}>{header}</span>
              </HeaderTitleContainer>

              <ActionButtonsContainer>
                {showActions && (
                  <>
                    {' '}
                    <CustomIcons.PenIcon
                      size="xs"
                      color={theme.colors.standardText}
                      onClick={(e?: React.MouseEvent) => {
                        e?.stopPropagation()
                        onEdit?.()
                      }}
                    />
                    <CustomIcons.TrashIcon
                      size="xs"
                      color={theme.colors.standardText}
                      onClick={(e?: React.MouseEvent) => {
                        e?.stopPropagation()
                        onDelete?.()
                      }}
                    />
                  </>
                )}

                {isActive ? (
                  <CustomIcons.ChevronUpIcon
                    size="xs"
                    color={theme.colors.standardText}
                  />
                ) : (
                  <CustomIcons.ChevronDownIcon
                    size="xs"
                    color={theme.colors.standardText}
                  />
                )}
              </ActionButtonsContainer>
            </DynamicHeader>
          )}
        >
          {/* Add text before children if provided */}
          <LineDivider />
          {text && <MainText hasChildren={!!children}>{text}</MainText>}
          {children}
        </BaseAccordion.Panel>
      </PanelContainer>
    )
  },
)

Panel.displayName = 'FAQPanel'

// Create the compound component
const FAQAccordionCompound = FAQAccordion as typeof FAQAccordion & {
  Panel: typeof Panel
}

FAQAccordionCompound.Panel = Panel

export default FAQAccordionCompound
